import apiClient from '@/lib/api-client';
import type {
  AccessLogEntry,
  BookingSessionNoteSummary,
  ClientPin,
  ClientRecord,
  NoteDocument,
  OverviewSession,
  SessionBooking,
  SessionClientRow,
  SessionCustomer,
  SessionMark,
  SessionNote,
  SessionNoteStatus,
  SessionsOverview,
  TimelineEntry,
} from '@/types/sessions';

/* eslint-disable @typescript-eslint/no-explicit-any -- backend payloads are snake_case JSON */

export function transformCustomer(c: any): SessionCustomer | null {
  if (!c) return null;
  return {
    id: c.id,
    publicId: c.public_id,
    firstName: c.first_name ?? '',
    lastName: c.last_name ?? '',
    fullName: c.full_name ?? '',
    initials: c.initials ?? '',
  };
}

export function transformBooking(b: any): SessionBooking {
  return {
    id: b.id,
    publicId: b.public_id,
    status: b.status,
    startTime: b.start_time,
    endTime: b.end_time,
    serviceName: b.service_name ?? null,
    durationMinutes: b.duration_minutes ?? null,
  };
}

export function transformMark(m: any): SessionMark {
  return {
    id: m.id,
    kind: m.kind,
    text: m.text,
    resolved: Boolean(m.resolved),
    resolvedAt: m.resolved_at ?? null,
    sessionNoteId: m.session_note_id ?? null,
    sessionDate: m.session_date ?? null,
  };
}

export function transformPin(p: any): ClientPin {
  return { id: p.id, text: p.text, position: p.position, createdAt: p.created_at };
}

export function transformNote(n: any): SessionNote {
  return {
    id: n.id,
    number: n.number ?? 1,
    status: n.status,
    content: n.content ?? null,
    completedAt: n.completed_at ?? null,
    lastEditedAt: n.last_edited_at ?? null,
    createdAt: n.created_at,
    booking: transformBooking(n.booking),
    customer: transformCustomer(n.customer),
    previousFollowUps: (n.previous_follow_ups ?? []).map(transformMark),
    pins: (n.pins ?? []).map(transformPin),
  };
}

function transformOverviewSession(s: any): OverviewSession {
  return {
    booking: transformBooking(s.booking),
    customer: transformCustomer(s.customer),
    note: s.note ? { id: s.note.id, status: s.note.status, lastEditedAt: s.note.last_edited_at ?? null } : null,
  };
}

function transformClientRow(c: any): SessionClientRow {
  return {
    ...transformCustomer(c)!,
    lastSessionAt: c.last_session_at ?? null,
    nextSessionAt: c.next_session_at ?? null,
    sessionsCount: c.sessions_count ?? 0,
    notesCount: c.notes_count ?? 0,
  };
}

export function transformOverview(o: any): SessionsOverview {
  return {
    today: (o.today ?? []).map(transformOverviewSession),
    toWrite: (o.to_write ?? []).map(transformOverviewSession),
    clients: (o.clients ?? []).map(transformClientRow),
    recentlyOpened: (o.recently_opened ?? []).map(transformClientRow),
    stats: {
      todayCount: o.stats?.today_count ?? 0,
      openNotesCount: o.stats?.open_notes_count ?? 0,
      weekCount: o.stats?.week_count ?? 0,
    },
  };
}

function transformTimelineEntry(e: any): TimelineEntry {
  return {
    id: e.id,
    number: e.number,
    status: e.status,
    booking: transformBooking(e.booking),
    excerpt: e.excerpt ?? null,
    lastEditedAt: e.last_edited_at ?? null,
  };
}

export function transformClientRecord(r: any): ClientRecord {
  return {
    customer: transformCustomer(r.customer)!,
    nextBooking: r.next_booking ? transformBooking(r.next_booking) : null,
    firstSessionAt: r.first_session_at ?? null,
    sessionsCount: r.sessions_count ?? 0,
    newNoteBooking: r.new_note_booking ? transformBooking(r.new_note_booking) : null,
    pins: (r.pins ?? []).map(transformPin),
    timeline: (r.timeline ?? []).map(transformTimelineEntry),
    highlights: (r.highlights ?? []).map(transformMark),
    openFollowUps: (r.open_follow_ups ?? []).map(transformMark),
  };
}

export function transformBookingNoteSummary(s: any): BookingSessionNoteSummary | null {
  if (!s) return null;
  return { status: s.status ?? null, id: s.id ?? null, mine: Boolean(s.mine), canWrite: Boolean(s.can_write) };
}

export interface NoteUpdateInput {
  content?: NoteDocument | null;
  status?: SessionNoteStatus;
}

class SessionsService {
  async getOverview(): Promise<SessionsOverview> {
    const { data } = await apiClient.get<{ data: any }>('/v1/sessions/overview');
    return transformOverview(data.data);
  }

  /** Opens the note for a booking, creating it (with the template) if needed. */
  async openNoteForBooking(bookingId: string | number, content?: NoteDocument): Promise<SessionNote> {
    const { data } = await apiClient.post<{ data: any }>('/v1/sessions/notes', { booking_id: bookingId, content });
    return transformNote(data.data);
  }

  async getNote(id: string): Promise<SessionNote> {
    const { data } = await apiClient.get<{ data: any }>(`/v1/sessions/notes/${id}`);
    return transformNote(data.data);
  }

  async updateNote(id: string, input: NoteUpdateInput): Promise<SessionNote> {
    const { data } = await apiClient.patch<{ data: any }>(`/v1/sessions/notes/${id}`, input);
    return transformNote(data.data);
  }

  async deleteNote(id: string): Promise<void> {
    await apiClient.delete(`/v1/sessions/notes/${id}`);
  }

  async getClientRecord(customerId: string | number): Promise<ClientRecord> {
    const { data } = await apiClient.get<{ data: any }>(`/v1/sessions/clients/${customerId}`);
    return transformClientRecord(data.data);
  }

  async getAccessLog(customerId: string | number): Promise<AccessLogEntry[]> {
    const { data } = await apiClient.get<{ data: any[] }>(`/v1/sessions/clients/${customerId}/access_log`);
    return data.data.map((e) => ({
      id: e.id,
      action: e.action,
      userName: e.user_name ?? null,
      sessionNoteId: e.session_note_id ?? null,
      createdAt: e.created_at,
    }));
  }

  async createPin(customerId: string | number, text: string): Promise<ClientPin> {
    const { data } = await apiClient.post<{ data: any }>(`/v1/sessions/clients/${customerId}/pins`, { text });
    return transformPin(data.data);
  }

  async updatePin(customerId: string | number, pinId: string, text: string): Promise<ClientPin> {
    const { data } = await apiClient.patch<{ data: any }>(`/v1/sessions/clients/${customerId}/pins/${pinId}`, { text });
    return transformPin(data.data);
  }

  async deletePin(customerId: string | number, pinId: string): Promise<void> {
    await apiClient.delete(`/v1/sessions/clients/${customerId}/pins/${pinId}`);
  }

  async setMarkResolved(markId: number, resolved: boolean): Promise<SessionMark> {
    const { data } = await apiClient.patch<{ data: any }>(`/v1/sessions/marks/${markId}`, { resolved });
    return transformMark(data.data);
  }
}

export const sessionsService = new SessionsService();
